/**
 * O modal "Novo Lançamento" / "Editar Lançamento".
 *
 * Papel: reproduz `#modal-entry` (l. 720–831) — Favorecido com a lista e o "+",
 * Data, Categoria e Subcategoria pelo tipo, Tipo, Forma de Pagamento, Valor,
 * Descrição, Status, Desconto, Acréscimo e o Valor Final, Observações, e as
 * seções de recorrência mensal e de parcelamento, que se excluem — e grava pelo
 * `salvarLancamentoSchema`, com os avisos de `saveEntry`.
 *
 * Como usar: quem monta passa o lançamento editado (ou nulo para criar), a casa
 * do caixa, o plano, os favorecidos e os padrões, e desmonta o modal em
 * `onFechar`. O modal só edita pendente; quem monta não o abre para o pago. As
 * regras ficam em `formulario-do-lancamento.ts`, e o modal só guarda os campos
 * e chama as regras.
 *
 * Digitar no Favorecido o nome exato de um favorecido — ou de um padrão —
 * aplica o perfil por cima do que estava e realça os campos trocados, até a
 * próxima mudança de cada um. O "+" cadastra o nome digitado e abre o perfil
 * por cima deste modal; salvar o perfil reaplica o autopreenchimento. "🗑
 * Excluir" apaga o editado onde ele estiver, em qualquer mês (exceção 3).
 *
 * As datas personalizadas e as caixas de marcar das duas seções não estão
 * dentro de `.field` na origem, e saem com o desenho do navegador.
 */
import {
  type Categoria,
  FORMA_PAGAMENTO_LANCAMENTO,
  type Favorecido,
  type FormaPagamentoLancamento,
  type LancamentoDaListagem,
  type PadraoDeFavorecido,
  ROTULOS_FORMA,
  type TipoLancamento,
} from '@concierge/shared';
import { useRef, useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { mascararCentavos } from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  type CampoDoPerfil,
  type IntervaloDoModal,
  type ValoresDoLancamento,
  aplicarPerfil,
  avisoDoSalvar,
  camposIguaisAoPerfil,
  categoriasDoTipo,
  comTipo,
  datasPersonalizadas,
  perfilDoNome,
  subcategoriasDoTipo,
  validarModal,
  valorFinalEscrito,
  valoresDoEditado,
  valoresDoNovo,
} from '../formulario-do-lancamento';
import {
  useCriarFavorecido,
  useExcluirLancamento,
  useSalvarLancamento,
} from '../hooks/usar-financas';
import { OPCOES_DE_TIPO_DO_MODAL } from '../rotulos';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import {
  CLASSE_DO_AUTOPREENCHIDO,
  CLASSE_DO_CONTROLE,
  CLASSE_DO_SELECT,
  Campo,
  LinhaDeCampos,
} from './kit/campo';
import { ModalDoPerfil } from './modal-do-perfil';

/** As opções de "Intervalo", na ordem da origem (l. 816–821). */
const OPCOES_DE_INTERVALO: readonly { valor: IntervaloDoModal; rotulo: string }[] = [
  { valor: 'mensal', rotulo: 'Mensal' },
  { valor: 'quinzenal', rotulo: 'Quinzenal (15 dias)' },
  { valor: 'semanal', rotulo: 'Semanal (7 dias)' },
  { valor: 'avulso', rotulo: 'Datas personalizadas' },
];

/** O texto de cada seção de baixo, com a borda de cima (l. 791, 804). */
const CLASSE_DA_SECAO = 'border-emb-borda mt-1 border-t pt-3';

/** O campo de data personalizada, com o contorno do navegador. */
const CLASSE_DA_DATA_DA_PARCELA =
  'w-[150px] rounded-[2px] border border-texto-fraco bg-superficie px-2 py-1 text-[12px]';

interface ModalDeLancamentoProps {
  /** O lançamento pendente editado; nulo para "Novo Lançamento". */
  editado: LancamentoDaListagem | null;
  residenciaId: string;
  plano: readonly Categoria[];
  favorecidos: readonly Favorecido[];
  padroes: readonly PadraoDeFavorecido[];
  onFechar: () => void;
  /** Recebe o identificador depois que o "🗑 Excluir" deu certo. */
  onExcluido?: (id: string) => void;
}

/**
 * O tipo escolhido no `select`.
 *
 * @param valor - O `value` da opção.
 * @returns O tipo do contrato; despesa para o desconhecido.
 */
function tipoEscolhido(valor: string): TipoLancamento {
  return OPCOES_DE_TIPO_DO_MODAL.find((opcao) => opcao.valor === valor)?.valor ?? 'despesa';
}

/**
 * A forma escolhida no `select`.
 *
 * @param valor - O `value` da opção.
 * @returns A forma do contrato; Pix para o desconhecido.
 */
function formaEscolhida(valor: string): FormaPagamentoLancamento {
  return FORMA_PAGAMENTO_LANCAMENTO.find((forma) => forma === valor) ?? 'pix';
}

/**
 * O intervalo escolhido no `select`.
 *
 * @param valor - O `value` da opção.
 * @returns O intervalo; mensal para o desconhecido.
 */
function intervaloEscolhido(valor: string): IntervaloDoModal {
  return OPCOES_DE_INTERVALO.find((opcao) => opcao.valor === valor)?.valor ?? 'mensal';
}

/**
 * O modal de lançamento.
 *
 * @param editado - O lançamento editado, ou nulo para criar.
 * @param residenciaId - A casa do caixa.
 * @param plano - O plano de contas.
 * @param favorecidos - Os favorecidos cadastrados.
 * @param padroes - Os padrões de favorecido.
 * @param onFechar - Desmonta o modal.
 * @param onExcluido - Recebe o identificador do lançamento excluído.
 * @returns A caixa de 640 px e, por cima dela, o perfil quando aberto.
 */
export function ModalDeLancamento({
  editado,
  residenciaId,
  plano,
  favorecidos,
  padroes,
  onFechar,
  onExcluido,
}: ModalDeLancamentoProps) {
  const notificar = useNotificacao();
  const salvar = useSalvarLancamento();
  const excluir = useExcluirLancamento();
  const criarFavorecido = useCriarFavorecido();

  const [valores, setValores] = useState<ValoresDoLancamento>(() =>
    editado === null ? valoresDoNovo(hojeEmIso(), plano) : valoresDoEditado(editado, plano),
  );
  const [realce, setRealce] = useState<ReadonlySet<CampoDoPerfil>>(() =>
    editado === null
      ? new Set()
      : camposIguaisAoPerfil(
          valoresDoEditado(editado, plano),
          perfilDoNome(editado.favorecido?.nome ?? '', favorecidos, padroes),
          plano,
        ),
  );
  const [perfilAberto, setPerfilAberto] = useState<Favorecido | null>(null);
  const gravando = useRef(false);

  const categorias = categoriasDoTipo(plano, valores.tipo);
  const subcategorias = subcategoriasDoTipo(plano, valores.tipo, valores.categoria);
  const nomesDaLista = [
    ...new Set([...favorecidos.map((item) => item.nome), ...padroes.map((item) => item.nome)]),
  ].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));

  /**
   * Troca campos e tira o realce dos que o perfil tinha preenchido.
   *
   * @param mudanca - Os campos que mudaram.
   * @param campos - Os campos com realce que esta mudança desfaz.
   */
  function mudar(mudanca: Partial<ValoresDoLancamento>, ...campos: CampoDoPerfil[]) {
    setValores((anteriores) => ({ ...anteriores, ...mudanca }));
    if (campos.length > 0) {
      setRealce(
        (anterior) => new Set([...anterior].filter((campo) => !campos.includes(campo))),
      );
    }
  }

  /**
   * Aplica o perfil do nome, quando há, sobre os campos.
   *
   * @param base - Os campos de partida, já com o nome digitado.
   * @param lista - Os favorecidos em que procurar o nome.
   */
  function autopreencher(base: ValoresDoLancamento, lista: readonly Favorecido[]) {
    const perfil = perfilDoNome(base.favorecido, lista, padroes);
    if (perfil === null) {
      setValores(base);
      return;
    }
    const resultado = aplicarPerfil(base, perfil, plano);
    setValores(resultado.valores);
    setRealce((anterior) => new Set([...anterior, ...resultado.campos]));
    if (resultado.avisar) notificar('✓ Perfil do favorecido aplicado');
  }

  /**
   * O "+": cadastra o nome digitado e abre o perfil (l. 5599–5615).
   *
   * Usa a trava do "Salvar": um segundo clique enquanto o cadastro não volta
   * mandaria o mesmo nome de novo, e a API o recusaria em vermelho.
   */
  async function handleAdicionarFavorecido() {
    if (gravando.current) return;
    const nome = valores.favorecido.trim();
    if (nome === '') {
      notificar('Digite o nome do favorecido', false);
      return;
    }
    const chave = nome.toLocaleLowerCase('pt-BR');
    if (favorecidos.some((item) => item.nome.toLocaleLowerCase('pt-BR') === chave)) {
      notificar('Favorecido já existe — perfil já cadastrado');
      return;
    }
    gravando.current = true;
    try {
      setPerfilAberto(await criarFavorecido.mutateAsync({ residenciaId, nome }));
    } catch (erro) {
      notificar(erro instanceof Error ? erro.message : 'Não foi possível gravar.', false);
    } finally {
      gravando.current = false;
    }
  }

  /**
   * Reaplica o autopreenchimento depois de salvar o perfil (l. 2157–2162).
   *
   * @param favorecido - O favorecido como ficou gravado.
   */
  function handlePerfilSalvo(favorecido: Favorecido) {
    if (valores.favorecido.trim() === '') return;
    autopreencher(valores, [
      favorecido,
      ...favorecidos.filter((item) => item.id !== favorecido.id),
    ]);
  }

  /**
   * Muda o número de parcelas e refaz as datas personalizadas.
   *
   * @param parcelas - O campo, como está.
   */
  function handleParcelas(parcelas: string) {
    setValores((anteriores) => ({
      ...anteriores,
      parcelas,
      datas:
        anteriores.intervalo === 'avulso'
          ? datasPersonalizadas(anteriores.datas, parcelas, anteriores.data, hojeEmIso())
          : anteriores.datas,
    }));
  }

  /**
   * Muda o intervalo; "Datas personalizadas" monta uma data por parcela.
   *
   * @param intervalo - O intervalo escolhido.
   */
  function handleIntervalo(intervalo: IntervaloDoModal) {
    setValores((anteriores) => ({
      ...anteriores,
      intervalo,
      datas:
        intervalo === 'avulso'
          ? datasPersonalizadas(
              anteriores.datas,
              anteriores.parcelas,
              anteriores.data,
              hojeEmIso(),
            )
          : anteriores.datas,
    }));
  }

  /**
   * Valida na ordem da origem e grava.
   *
   * Um segundo clique enquanto a gravação não volta não faz nada: sem a trava,
   * dois cliques rápidos criariam o lançamento duas vezes — e a recorrência,
   * as cópias em dobro.
   */
  async function handleSalvar() {
    if (gravando.current) return;
    const validado = validarModal(valores, residenciaId);
    if (!validado.valido) {
      notificar(validado.mensagem, false);
      return;
    }
    gravando.current = true;
    try {
      await salvar.mutateAsync({ id: editado?.id ?? null, corpo: validado.corpo });
      onFechar();
      notificar(avisoDoSalvar(validado.corpo.repeticao, editado !== null));
    } catch (erro) {
      notificar(erro instanceof Error ? erro.message : 'Não foi possível gravar.', false);
    } finally {
      gravando.current = false;
    }
  }

  /**
   * O "🗑 Excluir" do modal, que acha o lançamento em qualquer mês.
   *
   * Usa a trava do "Salvar": um segundo clique enquanto a exclusão não volta
   * perguntaria de novo e acabaria num 404 em vermelho.
   */
  async function handleExcluir() {
    if (editado === null || gravando.current) return;
    if (!window.confirm('Excluir este lançamento?')) return;
    gravando.current = true;
    try {
      await excluir.mutateAsync(editado.id);
      onFechar();
      onExcluido?.(editado.id);
      notificar('Lançamento excluído');
    } catch (erro) {
      notificar(erro instanceof Error ? erro.message : 'Não foi possível gravar.', false);
    } finally {
      gravando.current = false;
    }
  }

  return (
    <>
      <CaixaDeDialogo
        titulo={editado === null ? 'Novo Lançamento' : 'Editar Lançamento'}
        largura={640}
        onFechar={onFechar}
        rodape={
          <>
            {editado !== null && (
              <Botao
                variante="perigo"
                tamanho="sm"
                className="mr-auto"
                onClick={() => void handleExcluir()}
              >
                🗑 Excluir
              </Botao>
            )}
            <Botao variante="fantasma" onClick={onFechar}>
              Cancelar
            </Botao>
            <Botao onClick={() => void handleSalvar()} disabled={salvar.isPending}>
              Salvar
            </Botao>
          </>
        }
      >
        <LinhaDeCampos colunas={2}>
          <Campo rotulo="Favorecido" id="lancamento-favorecido">
            <div className="flex gap-1.5">
              <input
                id="lancamento-favorecido"
                list="lancamento-favorecidos"
                placeholder="Selecione ou digite..."
                autoComplete="off"
                value={valores.favorecido}
                onChange={(evento) =>
                  autopreencher({ ...valores, favorecido: evento.target.value }, favorecidos)
                }
                className={cn(CLASSE_DO_CONTROLE, 'flex-1')}
              />
              <datalist id="lancamento-favorecidos">
                {nomesDaLista.map((nome) => (
                  <option key={nome} value={nome} />
                ))}
              </datalist>
              <button
                type="button"
                title="Adicionar favorecido à lista"
                aria-label="Adicionar favorecido à lista"
                onClick={() => void handleAdicionarFavorecido()}
                className="shrink-0 cursor-pointer rounded-[6px] border border-emb-borda bg-superficie-2 px-2.5 [font-family:revert] text-[16px] [color:revert]"
              >
                +
              </button>
            </div>
          </Campo>
          <Campo rotulo="Data *" id="lancamento-data">
            <input
              id="lancamento-data"
              type="date"
              value={valores.data}
              onChange={(evento) => mudar({ data: evento.target.value })}
              className={CLASSE_DO_CONTROLE}
            />
          </Campo>
        </LinhaDeCampos>

        <LinhaDeCampos colunas={2}>
          <Campo rotulo="Categoria *" id="lancamento-categoria">
            <select
              id="lancamento-categoria"
              value={valores.categoria}
              onChange={(evento) =>
                mudar({ categoria: evento.target.value, subcategoria: '' }, 'categoria')
              }
              className={cn(
                CLASSE_DO_SELECT,
                realce.has('categoria') && CLASSE_DO_AUTOPREENCHIDO,
              )}
            >
              {categorias.map((categoria) => (
                <option key={categoria} value={categoria}>
                  {categoria}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Subcategoria" id="lancamento-subcategoria">
            <select
              id="lancamento-subcategoria"
              value={valores.subcategoria}
              onChange={(evento) =>
                mudar({ subcategoria: evento.target.value }, 'subcategoria')
              }
              className={cn(
                CLASSE_DO_SELECT,
                realce.has('subcategoria') && CLASSE_DO_AUTOPREENCHIDO,
              )}
            >
              <option value="">— Selecionar —</option>
              {subcategorias.map((subcategoria) => (
                <option key={subcategoria} value={subcategoria}>
                  {subcategoria}
                </option>
              ))}
            </select>
          </Campo>
        </LinhaDeCampos>

        <LinhaDeCampos colunas={3}>
          <Campo rotulo="Tipo *" id="lancamento-tipo">
            <select
              id="lancamento-tipo"
              value={valores.tipo}
              onChange={(evento) => {
                setValores((anteriores) =>
                  comTipo(anteriores, tipoEscolhido(evento.target.value), plano),
                );
                setRealce(
                  (anterior) => new Set([...anterior].filter((campo) => campo !== 'tipo')),
                );
              }}
              className={cn(CLASSE_DO_SELECT, realce.has('tipo') && CLASSE_DO_AUTOPREENCHIDO)}
            >
              {OPCOES_DE_TIPO_DO_MODAL.map((opcao) => (
                <option key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Forma de Pagamento" id="lancamento-forma">
            <select
              id="lancamento-forma"
              value={valores.formaPagamento}
              onChange={(evento) =>
                mudar(
                  { formaPagamento: formaEscolhida(evento.target.value) },
                  'formaPagamento',
                )
              }
              className={cn(
                CLASSE_DO_SELECT,
                realce.has('formaPagamento') && CLASSE_DO_AUTOPREENCHIDO,
              )}
            >
              {FORMA_PAGAMENTO_LANCAMENTO.map((forma) => (
                <option key={forma} value={forma}>
                  {ROTULOS_FORMA[forma]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Valor (R$) *" id="lancamento-valor">
            <input
              id="lancamento-valor"
              inputMode="decimal"
              placeholder="0,00"
              value={valores.valor}
              onChange={(evento) => mudar({ valor: mascararCentavos(evento.target.value) })}
              className={cn(CLASSE_DO_CONTROLE, 'text-right')}
            />
          </Campo>
        </LinhaDeCampos>

        <LinhaDeCampos colunas={2}>
          <Campo rotulo="Descrição *" id="lancamento-descricao">
            <input
              id="lancamento-descricao"
              placeholder="Ex: Mensalidade ref. Mai 2026"
              value={valores.descricao}
              onChange={(evento) => mudar({ descricao: evento.target.value })}
              className={CLASSE_DO_CONTROLE}
            />
          </Campo>
          <Campo rotulo="Status" id="lancamento-status">
            <select
              id="lancamento-status"
              value={valores.pago ? 'sim' : 'nao'}
              onChange={(evento) => mudar({ pago: evento.target.value === 'sim' })}
              className={CLASSE_DO_SELECT}
            >
              <option value="sim">✓ Pago / Confirmado</option>
              <option value="nao">⏳ Provisionado / A pagar</option>
            </select>
          </Campo>
        </LinhaDeCampos>

        <LinhaDeCampos colunas={3} className="items-end">
          <Campo rotulo="Desconto (R$)" id="lancamento-desconto">
            <input
              id="lancamento-desconto"
              inputMode="decimal"
              placeholder="0,00"
              value={valores.desconto}
              onChange={(evento) => mudar({ desconto: mascararCentavos(evento.target.value) })}
              className={cn(CLASSE_DO_CONTROLE, 'text-right')}
            />
          </Campo>
          <Campo rotulo="Acréscimo (R$)" id="lancamento-acrescimo">
            <input
              id="lancamento-acrescimo"
              inputMode="decimal"
              placeholder="0,00"
              value={valores.acrescimo}
              onChange={(evento) =>
                mudar({ acrescimo: mascararCentavos(evento.target.value) })
              }
              className={cn(CLASSE_DO_CONTROLE, 'text-right')}
            />
          </Campo>
          <div className="mb-[13px]">
            <span className="mb-1 block font-mono text-[10px] font-medium tracking-[0.04em] text-texto-fraco uppercase">
              Valor Final
            </span>
            <div className="py-2 font-sans text-[14px] font-bold text-foreground">
              {valorFinalEscrito(valores)}
            </div>
          </div>
        </LinhaDeCampos>

        <Campo rotulo="Observações" id="lancamento-observacao">
          <input
            id="lancamento-observacao"
            placeholder="(opcional)"
            value={valores.observacao}
            onChange={(evento) => mudar({ observacao: evento.target.value })}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>

        <div className={CLASSE_DA_SECAO}>
          <label className="flex cursor-pointer items-center gap-2 select-none">
            <input
              type="checkbox"
              checked={valores.recorrencia}
              onChange={(evento) =>
                mudar({
                  recorrencia: evento.target.checked,
                  parcelar: evento.target.checked ? false : valores.parcelar,
                })
              }
            />
            <span className="text-[13px] font-semibold text-foreground">
              Provisionar recorrência mensal
            </span>
          </label>
          {valores.recorrencia && (
            <div className="mt-2.5">
              <Campo
                rotulo="Repetir por quantos meses?"
                id="lancamento-meses"
                className="max-w-[200px]"
              >
                <input
                  id="lancamento-meses"
                  type="number"
                  min={1}
                  max={60}
                  placeholder="Ex: 12"
                  value={valores.meses}
                  onChange={(evento) => mudar({ meses: evento.target.value })}
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
              <div className="mt-1 text-[11px] text-texto-fraco">
                Serão criadas cópias provisionadas nos meses seguintes, com o mesmo valor e
                favorecido.
              </div>
            </div>
          )}
        </div>

        <div className={CLASSE_DA_SECAO}>
          <label className="flex cursor-pointer items-center gap-2 select-none">
            <input
              type="checkbox"
              checked={valores.parcelar}
              onChange={(evento) =>
                mudar({
                  parcelar: evento.target.checked,
                  recorrencia: evento.target.checked ? false : valores.recorrencia,
                })
              }
            />
            <span className="text-[13px] font-semibold text-foreground">
              Parcelar este lançamento
            </span>
          </label>
          {valores.parcelar && (
            <div className="mt-2.5 grid grid-cols-2 gap-3">
              <Campo rotulo="Número de Parcelas" id="lancamento-parcelas">
                <input
                  id="lancamento-parcelas"
                  type="number"
                  min={2}
                  max={60}
                  placeholder="Ex: 12"
                  value={valores.parcelas}
                  onChange={(evento) => handleParcelas(evento.target.value)}
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
              <Campo rotulo="Intervalo" id="lancamento-intervalo">
                <select
                  id="lancamento-intervalo"
                  value={valores.intervalo}
                  onChange={(evento) =>
                    handleIntervalo(intervaloEscolhido(evento.target.value))
                  }
                  className={CLASSE_DO_SELECT}
                >
                  {OPCOES_DE_INTERVALO.map((opcao) => (
                    <option key={opcao.valor} value={opcao.valor}>
                      {opcao.rotulo}
                    </option>
                  ))}
                </select>
              </Campo>
              {valores.intervalo === 'avulso' && (
                <div className="col-span-full mt-1">
                  <span className="mb-2 block text-[12px] text-texto-fraco">
                    Data de cada parcela
                  </span>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {valores.datas.map((data, indice) => (
                      // As datas não têm identidade além da posição: a lista se
                      // refaz por posição, como na origem.
                      <div key={indice} className="flex items-center gap-1.5">
                        <span className="min-w-5 text-right text-[12px] text-texto-fraco">
                          {indice + 1}ª
                        </span>
                        <input
                          type="date"
                          aria-label={`Data da ${String(indice + 1)}ª parcela`}
                          value={data}
                          onChange={(evento) =>
                            mudar({
                              datas: valores.datas.map((atual, posicao) =>
                                posicao === indice ? evento.target.value : atual,
                              ),
                            })
                          }
                          className={CLASSE_DA_DATA_DA_PARCELA}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CaixaDeDialogo>

      {perfilAberto !== null && (
        <ModalDoPerfil
          favorecido={perfilAberto}
          plano={plano}
          onFechar={() => setPerfilAberto(null)}
          onSalvo={handlePerfilSalvo}
        />
      )}
    </>
  );
}
